import Types from './types'
import Lang from './lang'
import Utilities from './utilities'

/**
 * Class to validate forms or fields
 * @module
 */
export default class Validate {
	/**
	 * 
	 * @param {HTMLElement} element - The container of the fields on which to perform the validation
	 * @param {boolean} scrollToError - Whether or not to scroll to the first field with error
	 * @param {boolean} highlightAll - Whether or not to perform the validation on all fields
	 * @param {func} fnOnError - Callback performed on fields with error
	 * @param {HTMLElement} scrollableParent - The element on which perform the scroll (scrollToError->true)
	 * @method
	 */
	static ValidateForm(element, scrollToError, highlightAll, fnOnError, scrollableParent) {
		// Opzioni di richiesta
		var options = {
			"scrollToError": scrollToError != null ? scrollToError : true, // Esegue lo scroll al primo elemento in errore
			"highlightAll": highlightAll || false, // Evidenzia tutti i campi errati, non solo il primo
		};

		var Form = element;
		var isValidForm = true;
		var FirstErrorToScroll = null;

		let vFields = Form.querySelectorAll('[data-validate]');

		for (let i = 0; i < vFields.length; i++) {
			var Field = vFields[i];
			if (Field.offsetWidth > 0 || Field.offsetHeight > 0) { // Vengono validati solo gli elementi visibili nel dom
				var isValidField = Validate.ValidateField(Field, fnOnError);

				if (isValidField == false || isValidField.IsValid == false) {
					isValidForm = false;

					if (FirstErrorToScroll == null)
						FirstErrorToScroll = Field;

					// Blocco il ciclo se il parametro è falso, altrimenti evidenzia di rosso tutti i campi non validi
					if (options.highlightAll == false)
						break;
				}
			}
		}

		// Funzione di scroll sul primo elemento non valido
		if (options.scrollToError == true && FirstErrorToScroll != null)
			if (scrollableParent)
				scrollableParent.scroll({
					top: FirstErrorToScroll.parentElement.offsetTop - scrollableParent.offsetTop,
					behavior: 'smooth'
				});
			else
				FirstErrorToScroll.parentElement.scrollIntoView({ behavior: "smooth" });

		return isValidForm;
	}

	/**
	 * 
	 * @param {HTMLElement} element - The field on which to perform the validation
	 * @param {func} fnOnError - Callback performed when validation fails
	 * @method
	 */
	static ValidateField(element, fnOnError) {
		var Field = element;
		var aRequiredValidationRules = Field.getAttribute("data-validate");
		var isRequired = Field.hasAttribute("required") || aRequiredValidationRules.includes("required") ? true : false;
		var isConditionalRequired = Field.hasAttribute("data-validate-requiredif") ? true : false;

		let ret = Validate._ValidateFieldByProps(Field, isRequired, isConditionalRequired, aRequiredValidationRules, fnOnError);

		return ret;
	}

	static _ValidateFieldByProps(element, isRequired, isConditionalRequired, aRequiredValidationRules, fnOnError) {
		var sDefaultErrorTxt = Validate._Ln("Il campo non è valido");
		var sDefaultEmptyTxt = Validate._Ln("Il campo non può essere vuoto");

		var Field = element;
		var val = Field.value;

		// Determino un array con i nomi delle regole di validazione effettivamente esistenti
		var aRealValidationRules = [];
		aRequiredValidationRules = aRequiredValidationRules.replace(/ /g, "").split(",");
		for (var index in aRequiredValidationRules) {
			var sRuleName = aRequiredValidationRules[index];
			if (Validation[sRuleName] != null)
				aRealValidationRules.push(sRuleName);
		}

		// Eseguo la diverse regole di validazione
		for (var index in aRealValidationRules) {
			var sRuleName = aRealValidationRules[index];

			// Required condizionale o meno: requiredif
			if (isConditionalRequired == true) {
				if (Validation[sRuleName].requiredif && typeof (Validation[sRuleName].requiredif) == "function") {
					var oValidationResult = Validation[sRuleName].requiredif(val, Field);
					if (oValidationResult == false) {
						var sErrorTxt = Validation[sRuleName].required || sDefaultEmptyTxt;
						if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
						return false;
					}
				}
			}
			else if (isRequired == true) {
				if (Validation[sRuleName].required && typeof (Validation[sRuleName].required) == "function") {
					var oValidationResult = Validation[sRuleName].required(val);
					if (oValidationResult.valid == false) {
						var sErrorTxt = oValidationResult.error || sDefaultEmptyTxt;
						if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
						return false;
					}
				}
				else {
					if (val == null || val.trim() == null || val.trim() == "") {
						var sErrorTxt = sDefaultEmptyTxt;
						if (Validation[sRuleName].required)
							sErrorTxt = Validation[sRuleName].required;
						if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
						return false;
					}
				}
			}

			// minlength
			if (Validation[sRuleName].minlength && Validation[sRuleName].minlength.length) {
				if (val.length < Validation[sRuleName].minlength.length) {
					var sErrorTxt = Validation[sRuleName].minlength.error || sDefaultErrorTxt;
					if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
					return false;
				}
			}

			// maxlength
			if (Validation[sRuleName].maxlength && Validation[sRuleName].maxlength.length) {
				if (val.length > Validation[sRuleName].maxlength.length) {
					var sErrorTxt = Validation[sRuleName].maxlength.error || sDefaultErrorTxt;
					if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
					return false;
				}
			}

			// custom
			if (Validation[sRuleName].custom && Validation[sRuleName].custom.func) {
				let validation = Validation[sRuleName].custom.func(val, Field);
				if (typeof validation == "string")
					return validation;
				else if (validation == false) {
					var sErrorTxt = Validation[sRuleName].custom.error || sDefaultErrorTxt;
					if (Types.ToString(Field.getAttribute("data-validate-error")) != "")
						sErrorTxt = Field.getAttribute("data-validate-error");
					if (typeof fnOnError === "function") { fnOnError(Field, sErrorTxt); }
					return false;
				}
			}
		}

		return true;
	}

	static _GetValidation() {
		return {
			// METODI DI BASE:
			// required,
			// requiredif,
			// minlength,
			// maxlength,
			"required": {
				required: Validate._Ln("Il campo non può essere vuoto")
			},

			"category": {
				required: Validate._Ln("Devi selezionare una categoria")
			},

			"number": { // Per i cellulari usare "phone"! Questo è un formato numerico intero o con virgola
				required: Validate._Ln("Inserisci un numero"),
				custom: {
					func: function (val) {
						if (val == null || val == "") // Il valore vuoto è controllato dal required
							return true;

						// numeri inclusi virgola e punto
						return /^[0-9.,]+$/.test(val);
					},
					error: Validate._Ln("Il campo non è in formato numerico"),
				}
			},

			"names": {
				custom: {
					func: function (val) {
						var regex = /^[^|\\!"£$%&/()+*\-/=?[\]@#0-9]*$/;
						return regex.test(val);
					},
					error: Validate._Ln("Il campo contiene caratteri non ammessi"),
				}
			},

			"repeatedchar": {
				custom: {
					func: function (val) {
						if (val.length > 3) {
							for (var i = 1; i < val.length; i++) {
								if (val[i] != val[0]) {
									return true;
								}
							}
							return false;
						} else {
							return true;
						}
					}
				},
			},

			"email": {
				required: Validate._Ln("L'email non può essere vuota"),
				custom: {
					func: function (val) {
						if (val == null || val == "") // Il valore vuoto è controllato dal required
							return true;

						// eslint-disable-next-line no-control-regex
						var regex = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
						return regex.test(val);
					},
					error: Validate._Ln("L'email non è in un formato valido"),
				},
			},

			"password": {
				required: Validate._Ln("La password non può essere vuota"),
				minlength: {
					length: 4,
					error: Validate._Ln("La password deve contenere almeno {0} caratteri").replace('{0}', '4')
				},
			},

			"password_strength": {
				required: Validate._Ln("La password non può essere vuota"),
				custom: {
					func: function (val) {
						var strongRegex = new RegExp("^(?=.{8,})(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*\\W).*$", "g");
						var mediumRegex = new RegExp("^(?=.{7,})(((?=.*[A-Z])(?=.*[a-z]))|((?=.*[A-Z])(?=.*[0-9]))|((?=.*[a-z])(?=.*[0-9]))).*$", "g");

						if (val.length == 0)
							return false;
						else if (strongRegex.test(val))
							return 'strong';
						else if (mediumRegex.test(val))
							return 'medium';
						else
							return 'weak';
					},
					error: Validate._Ln("La password non è in un formato valido")
				}
			},

			"confirmpassword": {
				custom: { // obbligatorio: attributo data-validate-equalto con selettore css
					func: function (val, El) {
						var sRef = El.getAttribute("data-validate-equalto");
						var Ref = document.querySelector(sRef);

						if (!Ref) {
							console.warn("data-validate-equalto param is not present or valid");
							return false;
						}

						if (val != Ref.value)
							return false;
						return true;
					},
					error: Validate._Ln("Le password devono corrispondere"),
				}
			},

			"uriname": {
				required: Validate._Ln("L'URL del canale è obbligatorio"),
				minlength: {
					length: 4,
					error: Validate._Ln("Il numero minimo di caratteri per l'Url del canale è {0}").replace('{0}', '4')
				},
				maxlength: {
					length: 32,
					error: Validate._Ln("Il numero massimo di caratteri per l'Url del canale è {0}").replace('{0}', '32')
				},
				custom: {
					func: function (val, El) {
						// Attivare per eliminare gli spazi in automatico e non mostrare l'errore
						//val = val.split(' ').join("");
						//El.val(val);

						var regex = new RegExp("^([a-z0-9_]+)$");
						return regex.test(val)
					},
					error: Validate._Ln("Utilizza solo lettere (a-z), numeri e _")
				}
			},

			"channelname": {
				required: Validate._Ln("Inserisci il nome canale"),
				maxlength: {
					length: 50,
					error: Validate._Ln("Il numero massimo di caratteri è {0}").replace('{0}', 50)
				}
			},

			"name": {
				required: Validate._Ln("Il nominativo non può essere vuoto"),
			},

			"firstname": {
				required: Validate._Ln("Il nome non può essere vuoto"),
			},

			"lastname": {
				required: Validate._Ln("Il cognome non può essere vuoto"),
			},

			"companyname": {
				required: Validate._Ln("Inserisci nome ditta"),
				requiredif: function (val, El) {
					var sRef = El.getAttribute("data-validate-requiredif");
					var Ref = document.querySelector(sRef);

					if (!Ref) {
						console.warn("data-validate-requiredif param is not valid");
						return false;
					}

					if (Types.ToInt(Ref.value) == 2 && (val == null || val == ""))
						return false;

					return true;
				},
			},

			"associationname": {
				required: Validate._Ln("Inserisci nome associazione"),
				requiredif: function (val, El) {
					var sRef = El.getAttribute("data-validate-requiredif");
					var Ref = document.querySelector(sRef);

					if (!Ref) {
						console.warn("data-validate-requiredif param is not present or valid");
						return false;
					}

					if (Types.ToInt(Ref.value) == 3 && (val == null || val == ""))
						return false;

					return true;
				},
			},

			"borndate": {
				required: Validate._Ln("La data di nascita non può essere vuota"),
				custom: { // opzionale: attributo data-validate-minage con selettore css
					func: function (val, El) {
						var sRef = El.getAttribute("data-validate-minage");
						if (sRef == null)
							return true;

						var date = val.split("/").reverse().join("-");
						var iAgeMinLimit = Types.ToInt(sRef);
						var iYearBorn = Types.ToInt(date.split("-")[0]);
						var iMonthBorn = Types.ToInt(date.split("-")[1]);
						var iDayBorn = Types.ToInt(date.split("-")[2]);
						var iYearNow = new Date().getFullYear();
						var iMonthNow = new Date().getMonth() + 1;
						var iDayNow = new Date().getDate();

						if (iYearNow - iYearBorn < iAgeMinLimit)
							return false;

						if (iYearNow - iYearBorn == iAgeMinLimit) {
							if (iMonthBorn > iMonthNow)
								return false;

							if (iDayBorn > iDayNow && iMonthBorn == iMonthNow)
								return false;
						}

						return true;
					},
					error: Validate._Ln("Età minima 16 anni"),
				}
			},

			"date": {
				required: Validate._Ln("La data non può essere vuota"),
			},

			"time": {
				required: Validate._Ln("L'orario non può essere vuoto"),
			},

			"address": {
				required: Validate._Ln("L'indirizzo non può essere vuoto"),
			},

			"city": {
				required: Validate._Ln("Il campo città non può essere vuoto"),
			},

			"province": {
				required: Validate._Ln("Il campo provincia non può essere vuoto"),
			},

			"phone": { // obbligatorio: attributo data-validate-isocode con selettore css
				required: Validate._Ln("Inserisci il numero"),
				custom: {
					func: function (val, El) {
						if (val == null || val == "") // Il valore vuoto è controllato dal required
							return true;

						var sRef = El.getAttribute("data-validate-isocode");
						var Ref = document.querySelector(sRef);

						if (!Ref) {
							console.warn("data-validate-isocode param is not present or valid");
							return false;
						}

						// Controllo che ci siano solo numeri
						if (/^[0-9]+$/.test(val) == false)
							return false;

						// Formatto il numero di telefono con il Trunk Prefix
						var sCountryIsoCodeNumber = Ref.value;
						var sFormattedNumber = Utilities.GetFormattedPhoneNumber(sCountryIsoCodeNumber, val, true);
						val = sFormattedNumber;
						El.value = sFormattedNumber;

						return true;
					},
					error: Validate._Ln("Numero non valido")
				},
			},

			"iban": {
				required: Validate._Ln("L'IBAN non può essere vuoto"),
				custom: {
					func: function (val) {
						// Remove spaces and to upper case
						var iban = val.replace(/ /g, "").toUpperCase(),
							ibancheckdigits = "",
							leadingZeroes = true,
							cRest = "",
							cOperator = "",
							countrycode, ibancheck, charAt, cChar, bbanpattern, bbancountrypatterns, ibanregexp, i, p;

						// Check for IBAN code length.
						// It contains:
						// country code ISO 3166-1 - two letters,
						// two check digits,
						// Basic Bank Account Number (BBAN) - up to 30 chars
						var minimalIBANlength = 5;
						if (iban.length < minimalIBANlength) {
							return false;
						}

						// Check the country code and find the country specific format
						countrycode = iban.substring(0, 2);
						bbancountrypatterns = {
							"AL": "\\d{8}[\\dA-Z]{16}",
							"AD": "\\d{8}[\\dA-Z]{12}",
							"AT": "\\d{16}",
							"AZ": "[\\dA-Z]{4}\\d{20}",
							"BE": "\\d{12}",
							"BH": "[A-Z]{4}[\\dA-Z]{14}",
							"BA": "\\d{16}",
							"BR": "\\d{23}[A-Z][\\dA-Z]",
							"BG": "[A-Z]{4}\\d{6}[\\dA-Z]{8}",
							"CR": "\\d{17}",
							"HR": "\\d{17}",
							"CY": "\\d{8}[\\dA-Z]{16}",
							"CZ": "\\d{20}",
							"DK": "\\d{14}",
							"DO": "[A-Z]{4}\\d{20}",
							"EE": "\\d{16}",
							"FO": "\\d{14}",
							"FI": "\\d{14}",
							"FR": "\\d{10}[\\dA-Z]{11}\\d{2}",
							"GE": "[\\dA-Z]{2}\\d{16}",
							"DE": "\\d{18}",
							"GI": "[A-Z]{4}[\\dA-Z]{15}",
							"GR": "\\d{7}[\\dA-Z]{16}",
							"GL": "\\d{14}",
							"GT": "[\\dA-Z]{4}[\\dA-Z]{20}",
							"HU": "\\d{24}",
							"IS": "\\d{22}",
							"IE": "[\\dA-Z]{4}\\d{14}",
							"IL": "\\d{19}",
							"IT": "[A-Z]\\d{10}[\\dA-Z]{12}",
							"KZ": "\\d{3}[\\dA-Z]{13}",
							"KW": "[A-Z]{4}[\\dA-Z]{22}",
							"LV": "[A-Z]{4}[\\dA-Z]{13}",
							"LB": "\\d{4}[\\dA-Z]{20}",
							"LI": "\\d{5}[\\dA-Z]{12}",
							"LT": "\\d{16}",
							"LU": "\\d{3}[\\dA-Z]{13}",
							"MK": "\\d{3}[\\dA-Z]{10}\\d{2}",
							"MT": "[A-Z]{4}\\d{5}[\\dA-Z]{18}",
							"MR": "\\d{23}",
							"MU": "[A-Z]{4}\\d{19}[A-Z]{3}",
							"MC": "\\d{10}[\\dA-Z]{11}\\d{2}",
							"MD": "[\\dA-Z]{2}\\d{18}",
							"ME": "\\d{18}",
							"NL": "[A-Z]{4}\\d{10}",
							"NO": "\\d{11}",
							"PK": "[\\dA-Z]{4}\\d{16}",
							"PS": "[\\dA-Z]{4}\\d{21}",
							"PL": "\\d{24}",
							"PT": "\\d{21}",
							"RO": "[A-Z]{4}[\\dA-Z]{16}",
							"SM": "[A-Z]\\d{10}[\\dA-Z]{12}",
							"SA": "\\d{2}[\\dA-Z]{18}",
							"RS": "\\d{18}",
							"SK": "\\d{20}",
							"SI": "\\d{15}",
							"ES": "\\d{20}",
							"SE": "\\d{20}",
							"CH": "\\d{5}[\\dA-Z]{12}",
							"TN": "\\d{20}",
							"TR": "\\d{5}[\\dA-Z]{17}",
							"AE": "\\d{3}\\d{16}",
							"GB": "[A-Z]{4}\\d{14}",
							"VG": "[\\dA-Z]{4}\\d{16}"
						};

						bbanpattern = bbancountrypatterns[countrycode];

						// As new countries will start using IBAN in the
						// future, we only check if the countrycode is known.
						// This prevents false negatives, while almost all
						// false positives introduced by this, will be caught
						// by the checksum validation below anyway.
						// Strict checking should return FALSE for unknown
						// countries.
						if (typeof bbanpattern !== "undefined") {
							ibanregexp = new RegExp("^[A-Z]{2}\\d{2}" + bbanpattern + "$", "");
							if (!(ibanregexp.test(iban))) {
								return false; // Invalid country specific format
							}
						}

						// Now check the checksum, first convert to digits
						ibancheck = iban.substring(4, iban.length) + iban.substring(0, 4);
						for (i = 0; i < ibancheck.length; i++) {
							charAt = ibancheck.charAt(i);
							if (charAt !== "0") {
								leadingZeroes = false;
							}
							if (!leadingZeroes) {
								ibancheckdigits += "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".indexOf(charAt);
							}
						}

						// Calculate the result of: ibancheckdigits % 97
						for (p = 0; p < ibancheckdigits.length; p++) {
							cChar = ibancheckdigits.charAt(p);
							cOperator = "" + cRest + "" + cChar;
							cRest = cOperator % 97;
						}
						return cRest === 1;
					},
					error: Validate._Ln("IBAN non valido")
				}
			},

			"bic": {
				required: Validate._Ln("Il codice BIC/SWIFT non può essere vuoto"),
				custom: {
					func: function (val) {
						var regex = /^([A-Z]{6}[A-Z2-9][A-NP-Z1-9])(X{3}|[A-WY-Z0-9][A-Z0-9]{2})?$/;
						return regex.test(val.toUpperCase());
					},
					error: Validate._Ln("Il codice BIC/SWIFT non è valido")
				}
			},

			"zipcode": {
				required: Validate._Ln("Il CAP non può essere vuoto"),
				custom: { // opzionale: attributo data-validate-country con selettore css
					func: function (val, El) {
						var sRef = El.getAttribute("data-validate-country");
						var sIsoCode = El.getAttribute("data-country-isocode");
						if (Types.ToString(sIsoCode).trim() == "" && Controller != null && Controller.User != null)
							sIsoCode = Controller.User.CountryIsoCode

						var Ref = document.querySelector(sRef);
						var sCountry = "";
						if (!Ref)
							sCountry = sIsoCode || "";
						else
							sCountry = Ref.value;

						// CASI
						switch (sCountry) {
							case "ITA":
								var regex = /^\d{5}$/;
								return regex.test(val);

							case "CAN":
								var regex = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ] *\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i;
								return regex.test(val);

							case "BRA":
								var regex = /^\d{2}.\d{3}-\d{3}?$|^\d{5}-?\d{3}?$/;
								return regex.test(val);

							case "NLD":
								var regex = /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/;
								return regex.test(val);

							case "GBR":
								var regex = /^((([A-PR-UWYZ][0-9])|([A-PR-UWYZ][0-9][0-9])|([A-PR-UWYZ][A-HK-Y][0-9])|([A-PR-UWYZ][A-HK-Y][0-9][0-9])|([A-PR-UWYZ][0-9][A-HJKSTUW])|([A-PR-UWYZ][A-HK-Y][0-9][ABEHMNPRVWXY]))\s?([0-9][ABD-HJLNP-UW-Z]{2})|(GIR)\s?(0AA))$/i;
								return regex.test(val);

							case "USA":
								var typeRegex = /^\d{5}(-\d{4})?$/;
								var rangeRegex = /^90[2-5]\d\{2\}-\d{4}$/;
								var isValidType = typeRegex.test(val);
								var isValidRange = rangeRegex.test(val);
								if (isValidType == true && isValidRange == true)
									return true;
								else
									return false;
						}

						return true;
					},
					error: Validate._Ln("CAP non valido"),
				}
			},

			"fiscalcode": {
				required: Validate._Ln("Il codice fiscale non può essere vuoto"),
				requiredif: function (val, El) {
					var sRef = El.getAttribute("data-validate-requiredif");
					if (sRef == null)
						return true;

					var Ref = document.querySelector(sRef);
					if (!Ref) {
						console.warn("data-validate-requiredif param is not valid");
						return false;
					}
					var sCountry = Ref.value;
					switch (sCountry) {
						case "ITA":
							if (val == "" || val.length == 0)
								return false;
							break;
					}
					return true;
				},
				custom: { // opzionale: attributo data-validate-country con selettore css
					func: function (val, El) {
						if (val == null || val == "") // Il valore vuoto è controllato dal required
							return true;

						var sRef = El.getAttribute("data-validate-country");
						var sIsoCode = El.getAttribute("data-country-isocode");
						if (Types.ToString(sIsoCode).trim() == "" && Controller != null && Controller.User != null)
							sIsoCode = Controller.User.CountryIsoCode
						var Ref = document.querySelector(sRef);
						var sCountry = "";

						if (!Ref)
							sCountry = sIsoCode || "";
						else
							sCountry = Ref.value;

						// CASI
						switch (sCountry) {
							case "ITA":

								if (val.length > 16)
									return false;

								var regex = /[A-Za-z]{6}[0-9lmnpqrstuvLMNPQRSTUV]{2}[abcdehlmprstABCDEHLMPRST]{1}[0-9lmnpqrstuvLMNPQRSTUV]{2}[A-Za-z]{1}[0-9lmnpqrstuvLMNPQRSTUV]{3}[A-Za-z]{1}/;
								var fiscalcodeChecksumITA = function (sFiscalCodeIta) {
									sFiscalCodeIta = sFiscalCodeIta.toUpperCase();
									var sum = 0;
									var CHECK_CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

									// Tavola conversione caratteri dispari
									var CHECK_CODE_ODD = {
										0: 1,
										1: 0,
										2: 5,
										3: 7,
										4: 9,
										5: 13,
										6: 15,
										7: 17,
										8: 19,
										9: 21,
										A: 1,
										B: 0,
										C: 5,
										D: 7,
										E: 9,
										F: 13,
										G: 15,
										H: 17,
										I: 19,
										J: 21,
										K: 2,
										L: 4,
										M: 18,
										N: 20,
										O: 11,
										P: 3,
										Q: 6,
										R: 8,
										S: 12,
										T: 14,
										U: 16,
										V: 10,
										W: 22,
										X: 25,
										Y: 24,
										Z: 23
									}

									// Tavola conversione caratteri pari
									var CHECK_CODE_EVEN = {
										0: 0,
										1: 1,
										2: 2,
										3: 3,
										4: 4,
										5: 5,
										6: 6,
										7: 7,
										8: 8,
										9: 9,
										A: 0,
										B: 1,
										C: 2,
										D: 3,
										E: 4,
										F: 5,
										G: 6,
										H: 7,
										I: 8,
										J: 9,
										K: 10,
										L: 11,
										M: 12,
										N: 13,
										O: 14,
										P: 15,
										Q: 16,
										R: 17,
										S: 18,
										T: 19,
										U: 20,
										V: 21,
										W: 22,
										X: 23,
										Y: 24,
										Z: 25
									};

									for (var i = 0; i < 15; i++) {
										var c = sFiscalCodeIta[i];
										sum += i % 2 ? CHECK_CODE_EVEN[c] : CHECK_CODE_ODD[c];
									}
									sum = sum % 26;

									if (CHECK_CODE_CHARS.charAt(sum) == sFiscalCodeIta.charAt(15))
										return true;
									return false;
								};

								if (regex.test(val) == false || fiscalcodeChecksumITA(val) == false)
									return false;
								break;
						}
						return true;
					},
					error: Validate._Ln("Codice fiscale non valido"),
				}
			},

			"vat": {
				required: Validate._Ln("La partita IVA non può essere vuota"),
				custom: { // opzionale: attributo data-validate-country con selettore css
					func: function (val, El) {
						var sRef = El.getAttribute("data-validate-country");
						var sIsoCode = El.getAttribute("data-country-isocode");
						if (Types.ToString(sIsoCode).trim() == "" && Controller != null && Controller.User != null)
							sIsoCode = Controller.User.CountryIsoCode
						var Ref = document.querySelector(sRef);
						var sCountry = "";
						if (!Ref)
							sCountry = sIsoCode || "";
						else
							sCountry = Ref.value;

						// CASI
						switch (sCountry) {
							case "ITA":
								if (val.length != 11)
									return false;

								var validi = "0123456789";
								for (var i = 0; i < 11; i++) {
									if (validi.indexOf(val.charAt(i)) == -1)
										return false;
								}
								var s = 0;
								for (i = 0; i <= 9; i += 2)
									s += val.charCodeAt(i) - '0'.charCodeAt(0);
								for (i = 1; i <= 9; i += 2) {
									var c = 2 * (val.charCodeAt(i) - '0'.charCodeAt(0));
									if (c > 9) c = c - 9;
									s += c;
								}
								if ((10 - s % 10) % 10 != val.charCodeAt(10) - '0'.charCodeAt(0))
									return false;
								break;
						}

						return true;
					},
					error: Validate._Ln("Partita IVA non valida"),
				}
			},

			"tag": {
				custom: {
					func: function (val, El) {
						if (val == "")
							return true;

						var regex = new RegExp("^([a-z0-9 _]+)$");
						return regex.test(val)
					},
					error: Validate._Ln("Utilizza solo lettere (a-z), numeri e _")
				}
			},

			"creditcard": {
				required: Validate._Ln("Inserisci il numero della carta di credito"),
				custom: {
					func: function (val, El) {
						// Accept only spaces, digits and dashes
						if (/[^0-9 \-]+/.test(val)) {
							return false;
						}

						var nCheck = 0,
							nDigit = 0,
							bEven = false,
							n, cDigit;

						val = val.replace(/\D/g, "");

						// Basing min and max length on
						// https://dev.ean.com/general-info/valid-card-types/
						if (val.length < 13 || val.length > 19) {
							return false;
						}

						for (n = val.length - 1; n >= 0; n--) {
							cDigit = val.charAt(n);
							nDigit = parseInt(cDigit, 10);
							if (bEven) {
								if ((nDigit *= 2) > 9) {
									nDigit -= 9;
								}
							}

							nCheck += nDigit;
							bEven = !bEven;
						}

						return (nCheck % 10) === 0;
					},
					error: Validate._Ln("Numero carta di credito non valido")
				}
			},

			"checked": {
				custom: {
					func: function (val, El) {
						if (El.checked == false)
							return false;
					},
				}
			},

			// TK: Fabio - CTBT-212 START
			"cvv": {
				custom: {
					func: function (val, El) {
						if (El.getAttribute("data-container") != "" && El.getAttribute("data-credit-card") != "") {
							var Wrapper = document.querySelector("#" + El.getAttribute("data-container"));
							if (Wrapper) {
								var CreditCard = Wrapper.querySelector("#" + El.getAttribute("data-credit-card"));
								if (CreditCard && (CreditCard.classList.contains("visa") || CreditCard.classList.contains("mastercard")) && val.length != 3)
									return false;
							}
						}
					},
					error: Validate._Ln("CVV non valido")
				}
			},
			// TK: Fabio - CTBT-212 END

			"url": {
				required: Validate._Ln("Il campo URL è obbligatorio"),
				custom: {
					func: function (val, El) {
						if (val == "")
							return true;

						return /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm.test(val);
					},
					error: Validate._Ln("URL non valido")
				}
			}
		};
	}

	static _Ln(sTxt) {
		let userLang = Lang.Get();

		if (Validate._GetTranslations() == null || Validate._GetTranslations()[sTxt] == null || Validate._GetTranslations()[sTxt][userLang] == null)
			return sTxt;

		return Validate._GetTranslations()[sTxt][userLang];
	}

	static _GetTranslations() {
		return {
			"Il campo non può essere vuoto": {
				"it": "Il campo non può essere vuoto",
				"en": "The field cannot be empty",
			},
			"Il campo non è valido": {
				"it": "Il campo non è valido",
				"en": "The field is not valid",
			},
			"Devi selezionare una categoria": {
				"it": "Devi selezionare una categoria",
				"en": "You have to select a category",
			},
			"Inserisci un numero": {
				"it": "Inserisci un numero",
				"en": "Enter a number",
			},
			"Il campo non è in formato numerico": {
				"it": "Il campo non è in formato numerico",
				"en": "The field is not in numeric format",
			},
			"Il campo contiene caratteri non ammessi": {
				"it": "Il campo contiene caratteri non ammessi",
				"en": "The field contains illegal characters",
			},
			"L'email non può essere vuota": {
				"it": "L'email non può essere vuota",
				"en": "The email cannot be empty",
			},
			"L'email non è in un formato valido": {
				"it": "L'email non è in un formato valido",
				"en": "The email is not in a valid format",
			},
			"La password non può essere vuota": {
				"it": "La password non può essere vuota",
				"en": "The password cannot be blank",
			},
			"La password deve contenere almeno {0} caratteri": {
				"it": "La password deve contenere almeno {0} caratteri",
				"en": "The password must contain at least {0} characters",
			},
			"La password non è in un formato valido": {
				"it": "La password non è in un formato valido",
				"en": "The password is not in a valid format",
			},
			"Le password devono corrispondere": {
				"it": "Le password devono corrispondere",
				"en": "Passwords must match",
			},
			"L'URL del canale è obbligatorio": {
				"it": "L'URL del canale è obbligatorio",
				"en": "The channel URL is required",
			},
			"Il numero minimo di caratteri per l'URL del canale è {0}": {
				"it": "Il numero minimo di caratteri per l'URL del canale è {0}",
				"en": "The minimum number of characters for the channel URL is {0}",
			},
			"Il numero massimo di caratteri per l'URL del canale è {0}": {
				"it": "Il numero massimo di caratteri per l'URL del canale è {0}",
				"en": "The maximum number of characters for the channel URL is {0}",
			},
			"Utilizza solo lettere (a-z), numeri e _": {
				"it": "Utilizza solo lettere (a-z), numeri e _",
				"en": "Use only letters (a-z), numbers and _",
			},
			"Inserisci il nome canale": {
				"it": "Inserisci il nome canale",
				"en": "Enter the channel name",
			},
			"Il numero massimo di caratteri è {0}": {
				"it": "Il numero massimo di caratteri è {0}",
				"en": "The maximum number of characters is {0}",
			},
			"Il nominativo non può essere vuoto": {
				"it": "Il nominativo non può essere vuoto",
				"en": "The name cannot be empty",
			},
			"Il nome non può essere vuoto": {
				"it": "Il nome non può essere vuoto",
				"en": "The name cannot be empty",
			},
			"Il cognome non può essere vuoto": {
				"it": "Il cognome non può essere vuoto",
				"en": "The surname cannot be empty",
			},
			"Inserisci nome ditta": {
				"it": "Inserisci nome ditta",
				"en": "Enter company name",
			},
			"Inserisci nome associazione": {
				"it": "Inserisci nome associazione",
				"en": "Enter association name",
			},
			"La data di nascita non può essere vuota": {
				"it": "La data di nascita non può essere vuota",
				"en": "The date of birth cannot be blank",
			},
			"Età minima 16 anni": {
				"it": "Età minima 16 anni",
				"en": "minimum age 16 yo",
			},
			"La data non può essere vuota": {
				"it": "La data non può essere vuota",
				"en": "The date cannot be blank",
			},
			"L'orario non può essere vuoto": {
				"it": "L'orario non può essere vuoto",
				"en": "The time cannot be empty",
			},
			"L'indirizzo non può essere vuoto": {
				"it": "L'indirizzo non può essere vuoto",
				"en": "The address cannot be empty",
			},
			"Il campo città non può essere vuoto": {
				"it": "Il campo città non può essere vuoto",
				"en": "The city field cannot be empty",
			},
			"Il campo provincia non può essere vuoto": {
				"it": "Il campo provincia non può essere vuoto",
				"en": "The province field cannot be empty",
			},
			"Inserisci il numero": {
				"it": "Inserisci il numero",
				"en": "Enter the number",
			},
			"Numero non valido": {
				"it": "Numero non valido",
				"en": "Invalid number",
			},
			"L'IBAN non può essere vuoto": {
				"it": "L'IBAN non può essere vuoto",
				"en": "IBAN cannot be empty",
			},
			"IBAN non valido": {
				"it": "IBAN non valido",
				"en": "Invalid IBAN",
			},
			"Il codice BIC/SWIFT non può essere vuoto": {
				"it": "Il codice BIC/SWIFT non può essere vuoto",
				"en": "The BIC/SWIFT code cannot be empty",
			},
			"Il codice BIC/SWIFT non è valido": {
				"it": "Il codice BIC/SWIFT non è valido",
				"en": "The BIC/SWIFT code is not valid",
			},
			"Il CAP non può essere vuoto": {
				"it": "Il CAP non può essere vuoto",
				"en": "The zip code cannot be empty",
			},
			"CAP non valido": {
				"it": "CAP non valido",
				"en": "Invalid zip code",
			},
			"Il codice fiscale non può essere vuoto": {
				"it": "Il codice fiscale non può essere vuoto",
				"en": "The tax code cannot be empty",
			},
			"Codice fiscale non valido": {
				"it": "Codice fiscale non valido",
				"en": "Invalid tax code",
			},
			"La partita IVA non può essere vuota": {
				"it": "La partita IVA non può essere vuota",
				"en": "The VAT number cannot be empty",
			},
			"Partita IVA non valida": {
				"it": "Partita IVA non valida",
				"en": "Invalid VAT number",
			},
			"Inserisci il numero della carta di credito": {
				"it": "Inserisci il numero della carta di credito",
				"en": "Enter your credit card number",
			},
			"Numero carta di credito non valido": {
				"it": "Numero carta di credito non valido",
				"en": "Invalid credit card number",
			},
			"CVV non valido": {
				"it": "CVV non valido",
				"en": "CVV invalid",
			},
			"Il campo URL è obbligatorio": {
				"it": "Il campo URL è obbligatorio",
				"en": "The URL field is required",
			},
			"URL non valido": {
				"it": "URL non valido",
				"en": "Invalid URL",
			}
		}
	}
}
let Validation = Validate._GetValidation();